import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import api from "../api";

export default function HeroSlider() {
  const [slides, setSlides] = useState([]);
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const progressInterval = useRef(null);

  useEffect(() => {
    let mounted = true;
    async function fetchSlides() {
      try {
        const res = await api.get("/api/hero/");
        const data = Array.isArray(res.data) ? res.data : [];
        if (mounted) setSlides(data);
      } catch (error) {
        console.error("Hero error:", error);
        if (mounted) setSlides([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchSlides();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (current >= slides.length && slides.length > 0) setCurrent(0);
  }, [slides.length, current]);

  const stopProgress = () => {
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
      progressInterval.current = null;
    }
  };

  const nextSlide = () => {
    if (!slides.length) return;
    setCurrent((prev) => (prev + 1) % slides.length);
    setProgress(0);
  };

  useEffect(() => {
    if (!slides.length) return undefined;
    stopProgress();
    const start = Date.now();
    progressInterval.current = setInterval(() => {
      const percent = Math.min(((Date.now() - start) / 6000) * 100, 100);
      setProgress(percent);
      if (percent >= 100) nextSlide();
    }, 50);
    return stopProgress;
  }, [current, slides.length]);

  if (loading) return <section className="h-[68vh] min-h-[520px] max-h-[760px] bg-[#F8F3E7] flex items-center justify-center"><div className="h-10 w-10 rounded-full border-4 border-[#0F766E]/25 border-t-[#0F766E] animate-spin" /></section>;
  if (!slides.length) return null;

  return (
    <section className="relative h-[68vh] min-h-[520px] max-h-[760px] w-full overflow-hidden bg-black">
      {slides.map((slide, index) => {
        const active = index === current;
        return (
          <motion.div key={slide.id || index} initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: active ? 1 : 0, scale: active ? 1 : 1.03 }} transition={{ duration: 1, ease: "easeInOut" }} className="absolute inset-0" aria-hidden={!active}>
            <img src={slide.image} alt={slide.title || "A1 Rugs handmade rug collection"} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-black/10" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/10" />
            <div className="relative mx-auto flex h-full max-w-7xl items-center px-5 sm:px-8 lg:px-10">
              <div className="max-w-2xl pb-8 text-white sm:pb-0">
                {slide.description && <motion.p initial={{ opacity: 0, y: 18 }} animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }} transition={{ duration: 0.7, delay: 0.15 }} className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#F8F3E7] sm:mb-5 sm:text-sm">{slide.description}</motion.p>}
                {slide.title && <motion.h1 initial={{ opacity: 0, y: 28 }} animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 28 }} transition={{ duration: 0.8, delay: 0.2 }} className="text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-7xl">{slide.title}</motion.h1>}
                {slide.buttons?.length > 0 && <motion.div initial={{ opacity: 0, y: 20 }} animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} transition={{ duration: 0.7, delay: 0.35 }} className="mt-7 flex flex-wrap gap-3 sm:mt-9 sm:gap-4">
                  {slide.buttons.map((btn, buttonIndex) => <a key={btn.id || buttonIndex} href={btn.link} className={`inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl sm:px-8 sm:py-3.5 sm:text-base ${buttonIndex === 0 ? "bg-[#F8F3E7] text-[#0F766E] hover:bg-[#0F766E] hover:text-white" : "border border-white/70 text-white hover:bg-white hover:text-[#0F766E]"}`}>{btn.text}</a>)}
                </motion.div>}
              </div>
            </div>
          </motion.div>
        );
      })}
      {slides.length > 1 && <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2.5 rounded-full bg-black/20 px-4 py-2 backdrop-blur-sm sm:bottom-8">
        {slides.map((slide, index) => <button key={slide.id || index} type="button" aria-label={`Go to slide ${index + 1}`} aria-current={index === current ? "true" : "false"} onClick={() => { setCurrent(index); setProgress(0); }} className="relative h-2 overflow-hidden rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/80" style={{ width: index === current ? 42 : 8 }}><span className="absolute inset-0 bg-white/45" /><span className="absolute inset-y-0 left-0 rounded-full bg-white" style={{ width: index === current ? `${progress}%` : index < current ? "100%" : "0%" }} /></button>)}
      </div>}
    </section>
  );
}
